import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download, ChevronLeft, Trophy, UserCheck, CalendarClock } from "lucide-react";
import { ReportDateFilter, useReportRange } from "../components/ReportDateFilter";


function downloadCsv(name, head, rows) {
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name}.csv`; a.click();
}

export default function StaffPopularity({ report, onBack }) {
  const { branchParam, selected, branches } = useBranch();
  const branchLabel = (selected && selected !== "all") ? (branches.find((b) => b.id === selected)?.name || "Your Branch") : "All Branches";
  const rr = useReportRange("month");
  const { start, end } = rr;
  const [flow, setFlow] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const params = { start, end, branch_id: branchParam };
    if (flow !== "all") params.booking_flow = flow;
    api.get("/reports/staff-popularity", { params }).then((r) => setData(r.data));
  }, [start, end, branchParam, flow]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const ft = data?.flow_totals || { STAFF_FIRST: 0, TIME_FIRST: 0 };

  const csvExport = () => downloadCsv("staff_popularity",
    ["Rank", "Staff", "Branch", "Total Customer Selections", "Staff-First", "Time-First", "Handled", "Completed", "Cancelled", "Selection %"],
    rows.map((r) => [r.rank, r.staff_name, r.branch, r.selections, r.staff_first, r.time_first, r.handled, r.completed, r.cancelled, r.selection_pct + "%"]));

  const medal = (rank) => rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;

  return (
    <div>
      <button onClick={onBack} data-testid="popularity-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"><ChevronLeft size={16} /> All Reports</button>
      <PageHeader title="Staff Appointment Popularity" subtitle="Ranks staff by how often CUSTOMERS personally chose them (Staff-First + Time-First). System/staff-assigned bookings don't count."
        actions={<Button onClick={csvExport} variant="outline" className="rounded-sm" data-testid="popularity-csv"><Download size={15} className="mr-1" /> Excel/CSV</Button>} />

      <Card className="p-4 bg-card border-border mb-5">
        <Badge variant="outline" className="border-primary text-primary mb-3" data-testid="popularity-branch-chip">Showing: {branchLabel}</Badge>
        <div className="flex flex-wrap items-end gap-2">
          <ReportDateFilter r={rr} prefix="popularity" className="flex-1" />
          <div className="ml-auto">
            <Select value={flow} onValueChange={setFlow}>
              <SelectTrigger className="bg-secondary w-44" data-testid="popularity-flow"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Booking Flows</SelectItem>
                <SelectItem value="STAFF_FIRST">Staff First</SelectItem>
                <SelectItem value="TIME_FIRST">Time First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Customer Selections" value={data?.total_selections ?? 0} icon={UserCheck} testid="popularity-kpi-total" />
        <StatCard label="Staff-First Bookings" value={ft.STAFF_FIRST} icon={Trophy} testid="popularity-kpi-sf" />
        <StatCard label="Time-First Bookings" value={ft.TIME_FIRST} icon={CalendarClock} testid="popularity-kpi-tf" />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead><tr className="text-muted-foreground text-left border-b border-border">
            <th className="py-2 px-3">Rank</th><th className="px-3">Staff</th><th className="px-3">Branch</th>
            <th className="text-right px-3">Customer Selections</th><th className="text-right px-3">Staff-First</th>
            <th className="text-right px-3">Time-First</th><th className="text-right px-3">Handled</th>
            <th className="text-right px-3">Completed</th><th className="text-right px-3">Cancelled</th><th className="text-right px-3">Selection %</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.staff_id} className="border-b border-border/50" data-testid={`popularity-row-${r.staff_id}`}>
                <td className="py-2 px-3 text-lg">{medal(r.rank)}</td>
                <td className="px-3">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8"><AvatarImage src={r.photo} /><AvatarFallback className="text-xs bg-secondary">{(r.staff_name || "?").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                    <span className="font-medium">{r.staff_name}</span>
                  </div>
                </td>
                <td className="px-3 text-muted-foreground">{r.branch}</td>
                <td className="text-right px-3 text-primary font-semibold">{r.selections}</td>
                <td className="text-right px-3">{r.staff_first}</td>
                <td className="text-right px-3">{r.time_first}</td>
                <td className="text-right px-3">{r.handled}</td>
                <td className="text-right px-3 text-green-400">{r.completed}</td>
                <td className="text-right px-3 text-red-400">{r.cancelled}</td>
                <td className="text-right px-3">{r.selection_pct}%</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className="text-center text-muted-foreground py-10">No customer-selected appointments in this range.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
